'use client';

import { alpha, Box, Button, Typography, type SxProps } from '@mui/material';
import Slide from '@mui/material/Slide';
import { flatSx } from '@ocodelib/mui';
import { useHover, useSize } from 'ahooks';
import { useEffect, useRef, useState } from 'react';
import { useP5BasicApp } from '../../useP5BasicApp';

interface Props {
  sx?: SxProps;
}

export function LayoutRightPane(props: Props) {
  const { sx } = props;
  const { app } = useP5BasicApp();
  const snapshotImageRef = useRef<HTMLImageElement | null>(null);
  const iframeElemRef = useRef<HTMLIFrameElement | null>(null);
  const iframeSize = useSize(iframeElemRef);
  const [snapshotImageUrl, setSnapshotImageUrl] = useState<string | null>(null);
  const snapshotImageHovering = useHover(snapshotImageRef);

  useEffect(() => {
    app.setExecIframeDom(iframeElemRef.current);
  }, [app]);

  useEffect(() => {
    const s1 = app.snapshotImageUrl$.subscribe(setSnapshotImageUrl);
    return () => {
      s1.unsubscribe();
    };
  }, [app]);

  return (
    <Box
      className="LayoutRightPane-root"
      sx={flatSx(
        {
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '& .LayoutRightPane-execIframe': {
            flex: 1,
            m: 1,
            width: '100%',
          },
        },
        sx,
      )}
    >
      <Box
        sx={{
          height: 40,
          bgcolor: '#444',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pl: 2,
        }}
      >
        <Typography
          component="div"
          sx={{
            '& small': {
              display: 'inline-block',
              ml: 1,
              fontSize: '0.8rem',
              letterSpacing: '1px',
              color: (theme) => alpha(theme.palette.primary.contrastText, 0.9),
            },
          }}
        >
          실행 결과
          {iframeSize && (
            <small>
              {iframeSize.width} x {iframeSize.height}
            </small>
          )}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Button
            onClick={() => {
              setSnapshotImageUrl(null);
              setTimeout(() => {
                app.snapshot();
              }, 400);
            }}
          >
            스냅샷
          </Button>
          {snapshotImageUrl && (
            <Slide direction="left" in>
              <Box
                sx={{
                  position: 'relative',
                  width: 50,
                  height: 38,
                  '& img': {
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    objectFit: 'cover',
                    border: '1px solid #fff8',
                  },
                }}
              >
                <img
                  ref={snapshotImageRef}
                  src={snapshotImageUrl}
                  width={50}
                  height={36}
                  alt="snapshot"
                  className="lightbox"
                />
              </Box>
            </Slide>
          )}
        </Box>
      </Box>
      {snapshotImageUrl && snapshotImageHovering && (
        <Box
          sx={{
            position: 'absolute',
            top: 42,
            right: 4,
            width: 200,
            '& img': {
              width: '100%',
              height: 'auto',
            },
          }}
        >
          <img src={snapshotImageUrl} alt="snapshot" />
        </Box>
      )}
      <iframe
        title="layout right"
        ref={iframeElemRef}
        frameBorder="none"
        className="LayoutRightPane-execIframe"
      />
    </Box>
  );
}
